/**
 * Schémas Zod pour valider la sortie JSON de Claude.
 *
 * Claude est instruit de produire un JSON conforme — mais on ne lui fait
 * pas confiance aveuglément. Toute sortie est passée par Zod, refusant
 * silencieusement les hallucinations (steps avec 0 réponse correcte,
 * difficulté inconnue, MD trop long…).
 */

import { z } from 'zod';

export const ERROR_TYPES = ['calcul', 'raisonnement', 'lecture', 'signe'] as const;
export type ErrorType = (typeof ERROR_TYPES)[number];

const graphFunctionSchema = z.object({
  fn: z.string().min(1).max(300),
  color: z.string().max(30).optional().default('#3b82f6'),
  label: z.string().max(50).nullable().optional().default(null),
  dashed: z.boolean().optional().default(false),
});

const graphPointSchema = z.object({
  x: z.number(),
  y: z.number(),
  label: z.string().max(100).nullable().optional().default(null),
  color: z.string().max(30).optional().default('#ef4444'),
});

export const graphConfigSchema = z
  .object({
    functions: z.array(graphFunctionSchema).min(1).max(8),
    xMin: z.number().default(-5),
    xMax: z.number().default(5),
    yMin: z.number().nullable().optional().default(null),
    yMax: z.number().nullable().optional().default(null),
    points: z.array(graphPointSchema).optional().default([]),
    caption: z.string().max(500).nullable().optional().default(null),
    zoom: z.boolean().optional().default(true),
  })
  .nullable()
  .optional()
  .default(null);

export type GraphConfig = z.infer<typeof graphConfigSchema>;

/** Une réponse possible à une étape (QCM). */
export const choiceSchema = z
  .object({
    label_md: z.string().min(1).max(1024),
    is_correct: z.boolean(),
    error_type: z.enum(ERROR_TYPES).nullable().optional().default(null),
    feedback_md: z.string().max(2048).nullable().optional().default(null),
  })
  // Cohérence pédagogique : error_type renseigné SSI réponse fausse.
  .refine(
    (c) => (c.is_correct ? c.error_type === null : true),
    'is_correct=true ne doit pas avoir error_type',
  );

/** Une étape du Solve Path. */
export const stepSchema = z
  .object({
    prompt_md: z.string().min(0).max(8192),
    explanation_md: z.string().min(1).max(8192),
    hint_md: z.string().max(2048).nullable().optional().default(null),
    /** Slug d'une leçon du même chapitre vers laquelle pointer en cas d'échec. */
    lesson_slug: z.string().max(80).nullable().optional().default(null),
    /**
     * Extrait markdown de la leçon à afficher en popup sur cette étape.
     * Si null et lesson_slug renseigné → affiche la leçon complète.
     * Si null et lesson_slug null → affiche toutes les leçons du chapitre.
     */
    lesson_excerpt_md: z.string().max(8192).nullable().optional().default(null),
    /**
     * Sous-question introduite au début de cette étape.
     * null = étape de continuation (même question que la précédente).
     * Révélée à l'élève uniquement quand il atteint cette étape.
     */
    sub_statement_md: z.string().max(4096).nullable().optional().default(null),
    /**
     * Niveau hiérarchique de la sous-question (si sub_statement_md est non null).
     *   1 = premier niveau  (Partie I, Question 1, …)
     *   2 = second niveau   (a), b), …)
     *   3 = troisième niveau (i., ii., …)
     * null si cette étape ne débute pas une nouvelle question.
     */
    part_level: z.number().int().min(1).max(3).nullable().optional().default(null),
    /** Mode step-by-step — état de l'équation AVANT la transformation. */
    equation_before_md: z.string().max(2048).nullable().optional().default(null),
    /** Mode step-by-step — état de l'équation APRÈS (révélé post-réponse). */
    equation_after_md: z.string().max(2048).nullable().optional().default(null),
    /** Mode step-by-step — description courte de la transformation (3-6 mots). */
    transformation_label_md: z.string().max(256).nullable().optional().default(null),
    choices: z.array(choiceSchema).min(2).max(6),
    graph_config: graphConfigSchema,
    /** IDs des blocs visuels de l'exercice référencés par cette étape. */
    referenced_visual_block_ids: z.array(z.string()).optional().default([]),
    /** Bloc et objet à mettre en évidence quand l'élève arrive sur cette étape. */
    focus_target: z
      .object({ block_id: z.string(), object_id: z.string().optional() })
      .nullable()
      .optional()
      .default(null),
  })
  // Une et une seule réponse correcte — la base d'un QCM bien formé.
  .refine(
    (s) => s.choices.filter((c) => c.is_correct).length === 1,
    'chaque step doit avoir exactement 1 choix correct',
  );

/** Le Solve Path complet généré. */
export const solvePathOutputSchema = z.object({
  title: z.string().min(1).max(200),
  statement_md: z.string().min(1).max(16384),
  /**
   * Contexte commun affiché en permanence pendant l'exercice (données,
   * figure, mise en situation). Ne contient PAS les sous-questions.
   * null si l'exercice n'a pas de contexte séparé.
   */
  context_md: z.string().max(16384).nullable().optional().default(null),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  estimated_minutes: z.number().int().min(1).max(120),
  /** true si l'exercice est une résolution d'équation étape par étape. */
  step_by_step_mode: z.boolean().optional().default(false),
  steps: z.array(stepSchema).min(2).max(8),
  graph_config: graphConfigSchema,
});

export type SolvePathOutput = z.infer<typeof solvePathOutputSchema>;
export type StepOutput = z.infer<typeof stepSchema>;
export type ChoiceOutput = z.infer<typeof choiceSchema>;

// ─── Exercice fondamental (banque de questions Warmup) ────────────────────────
// Un exercice fondamental est une série de questions QCM INDÉPENDANTES :
// pas d'équation pas-à-pas, pas de sous-questions hiérarchiques, pas de
// dépendance entre questions. Chaque question devient un exercise_step autonome.

export const FUNDAMENTAL_MODES = [
  'LESSON_RECALL',
  'CALCULATION',
  'FORMULA',
  'THEOREM',
  'MIXED',
] as const;
export type FundamentalMode = (typeof FUNDAMENTAL_MODES)[number];

/** Une question QCM autonome d'un exercice fondamental. */
export const fundamentalQuestionSchema = z
  .object({
    prompt_md: z.string().min(1).max(8192),
    explanation_md: z.string().min(1).max(8192),
    hint_md: z.string().max(2048).nullable().optional().default(null),
    /** Difficulté propre à la question. null = hérite de la difficulté globale. */
    difficulty: z.enum(['easy', 'medium', 'hard']).nullable().optional().default(null),
    /** Slug indicatif d'une compétence du chapitre — résolu en competence_id à l'enregistrement. */
    competence_slug: z.string().max(80).nullable().optional().default(null),
    choices: z.array(choiceSchema).min(3).max(4),
  })
  .refine(
    (q) => q.choices.filter((c) => c.is_correct).length === 1,
    'chaque question doit avoir exactement 1 choix correct',
  );

/** Un exercice fondamental complet : titre + série de questions indépendantes. */
export const fundamentalExerciseOutputSchema = z.object({
  title: z.string().min(1).max(200),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  estimated_minutes: z.number().int().min(1).max(120),
  fundamental_mode: z.enum(FUNDAMENTAL_MODES).optional().default('MIXED'),
  questions: z.array(fundamentalQuestionSchema).min(1).max(40),
});

export type FundamentalExerciseOutput = z.infer<typeof fundamentalExerciseOutputSchema>;
export type FundamentalQuestionOutput = z.infer<typeof fundamentalQuestionSchema>;
